import useComponents from '@/use/components';
import useRouter from '@/use/router';
import useStore from '@/use/store';
import usePlugins from '@/use/plugins';
import useManager from '@/use/manager';
import { emitter } from '@/utils/event/emitter.ts';
import { APP } from '@/utils/event/constants.ts';
import { hasAppModule, registerAppModule, resolveAppModuleService } from '@/modules/registry';

const uses = {
    components: useComponents,
    router: useRouter,
    store: useStore,
    plugins: usePlugins,
    manager: useManager,
};

export const useModule = useList => (context, { name, module }) => {
    if (hasAppModule(name)) return;

    const useData = Object.entries(useList).reduce((moduleData, [prop, callback]) => {
        const moduleUse = module.use?.[prop];

        if (!moduleUse) return moduleData;

        moduleData[prop] = callback(context, name, moduleUse);

        return moduleData;
    }, {});

    const { service, ...moduleData } = module;
    const registerData = {
        ...moduleData,
        name,
        use: useData,
        service: resolveAppModuleService(service, context),
    };

    registerAppModule(registerData);

    return registerData;
};

export default (context, modules) => {
    const imported = modules.map(({ NAME: name, default: module }) => {
        try {
            const registeredData = useModule(uses)(context, { name, module });

            if (!registeredData) return null;

            if (!import.meta.env.PROD) {
                // eslint-disable-next-line no-console
                console.info(`Register app module: ${name}`);
            }

            return name;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Registering app module error: ${name}\n${e.toString()}`);

            return null;
        }
    }).filter(Boolean);

    if (imported.length) {
        emitter.emit(APP.MODULES);
    }
};
